/* ============================================================
   ZNAKI · audio/chain.js
   Łańcuch przetwarzania — wspólny dla pojedynczego dźwięku (Voice)
   i dla całej grupy (Bus, np. "tło", "akcenty"):

     wejście → filtr dolnoprzepustowy → panorama → głośność → wyjście

   Filtr jest zawsze w torze, ale domyślnie otwarty (20 kHz), więc
   nie zabarwia dźwięku, dopóki ktoś go nie przymknie.
   ============================================================ */

/* powyżej tej częstotliwości filtr jest praktycznie przezroczysty */
export const FILTER_OPEN = 20000;

/* domyślna stała czasowa dopasowania wartości sterowanych na żywo [s] —
   krótka, ale na tyle długa, by nie było "schodków" (zipper noise) */
const SMOOTH_TC = 0.03;

class AudioChain {

  /**
   * @param {AudioContext} ctx
   * @param {object} opts — { volume, pan, cutoff, q }
   */
  constructor(ctx, opts = {}) {
    const { volume = 1, pan = 0, cutoff = 0, q = 0.7 } = opts;

    this.ctx = ctx;

    this.filter = ctx.createBiquadFilter();
    this.filter.type = 'lowpass';
    this.filter.frequency.value = cutoff > 0 ? cutoff : FILTER_OPEN;
    this.filter.Q.value = q;

    /* StereoPannerNode bywa nieobecny w starszych przeglądarkach —
       wtedy po prostu pomijamy panoramę */
    this.panner = ctx.createStereoPanner ? ctx.createStereoPanner() : null;
    if (this.panner) this.panner.pan.value = pan;

    this.gain = ctx.createGain();
    this.gain.gain.value = volume;

    /* wszystko, co gra przez ten łańcuch, wpina się w this.input */
    this.input = this.filter;
    this.output = this.gain;

    /* ostatnio zadane wartości — pętla sceny woła settery co klatkę,
       a nie ma sensu zaśmiecać osi czasu Web Audio zdarzeniem bez zmiany */
    this._lastVolume = volume;
    this._lastPan = pan;
    this._lastCutoff = this.filter.frequency.value;
    this._lastQ = q;

    if (this.panner) {
      this.filter.connect(this.panner);
      this.panner.connect(this.gain);
    } else {
      this.filter.connect(this.gain);
    }
  }

  /* --- podłączenie ------------------------------------------- */

  connect(target) {
    this.gain.connect(target && target.input ? target.input : target);
    return this;
  }

  disconnect() {
    try { this.gain.disconnect(); } catch (e) { /* już odpięty */ }
    return this;
  }

  /* --- sterowanie na żywo ------------------------------------ */

  /** głośność 0–1; `tc` = stała czasowa dojścia [s] (0 = natychmiast) */
  setVolume(v, tc = SMOOTH_TC) {
    const val = Math.max(0, v);
    if (near(val, this._lastVolume)) return this;
    this._lastVolume = val;
    this._target(this.gain.gain, val, tc);
    return this;
  }

  /** panorama -1 (lewo) … 1 (prawo) */
  setPan(p, tc = SMOOTH_TC) {
    if (!this.panner) return this;
    const val = Math.max(-1, Math.min(1, p));
    if (near(val, this._lastPan)) return this;
    this._lastPan = val;
    this._target(this.panner.pan, val, tc);
    return this;
  }

  /** próg filtra [Hz]; 0 lub wartość ≥ FILTER_OPEN = filtr otwarty */
  setCutoff(hz, tc = SMOOTH_TC) {
    const f = (!hz || hz >= FILTER_OPEN) ? FILTER_OPEN : Math.max(40, hz);
    if (near(f, this._lastCutoff, 0.5)) return this;
    this._lastCutoff = f;
    this._target(this.filter.frequency, f, tc);
    return this;
  }

  /** rezonans filtra — im wyżej, tym wyraźniejszy sweep */
  setQ(q, tc = SMOOTH_TC) {
    const val = Math.max(0.0001, q);
    if (near(val, this._lastQ, 0.005)) return this;
    this._lastQ = val;
    this._target(this.filter.Q, val, tc);
    return this;
  }

  /* --- zdarzenia (obwiednie) ---------------------------------- */

  /**
   * Płynne przejście głośności do zadanej wartości w czasie `dur` [s].
   * Używane do wejść i wyjść dźwięków — w odróżnieniu od setVolume(),
   * które śledzi wartość sterowaną co klatkę.
   */
  fade(to, dur = 1, when = 0) {
    const t0 = Math.max(when || 0, this.ctx.currentTime);
    const p = this.gain.gain;
    const from = p.value;
    p.cancelScheduledValues(t0);
    p.setValueAtTime(from, t0);
    if (dur > 0) p.linearRampToValueAtTime(Math.max(0, to), t0 + dur);
    else p.setValueAtTime(Math.max(0, to), t0);
    this._lastVolume = null;   /* obwiednia przejmuje sterowanie */
    return this;
  }

  /** przerwij zaplanowane obwiednie i zatrzymaj głośność na bieżącej wartości */
  cancelRamps() {
    const now = this.ctx.currentTime;
    const p = this.gain.gain;
    const v = p.value;
    p.cancelScheduledValues(now);
    p.setValueAtTime(v, now);
    this._lastVolume = v;
    return this;
  }

  get volume() { return this.gain.gain.value; }

  /* --- wewnętrzne --------------------------------------------- */

  _target(param, value, tc) {
    const now = this.ctx.currentTime;
    if (!tc) {
      param.cancelScheduledValues(now);
      param.setValueAtTime(value, now);
    } else {
      /* setTargetAtTime nie wymaga anulowania — dokłada się do bieżącej wartości */
      param.setTargetAtTime(value, now, tc);
    }
  }

}

/* porównanie z tolerancją — chroni przed zdarzeniem na każdą klatkę */
const near = (a, b, eps = 0.0005) => b !== null && b !== undefined && Math.abs(a - b) < eps;

export default AudioChain;
